import { inject, Injectable } from '@angular/core'

import { BehaviorSubject, takeUntil } from 'rxjs'

import { Memoize } from '@ti/core/common/decorators'
import { WhiteLabelSettingsProvider } from '@ti/core/common/services'
import { BrowserLocalStorage } from '@ti/core/common/storage'
import { exposeReadonlyState, toSignalState } from '@ti/core/common/utils'
import { ViewModel } from '@ti/core/models/view.model'

import { Shade, ThemeName } from '../contracts'

type Palette = Record<number, string>

interface StoredPalette {
  theme: ThemeName | string
  palette: Palette
}

export const PRE_DEFIED_COLOR_SCHEMES: Record<ThemeName, string> = {
  purple: '#8B5CF6',
  darkBlue: '#5766C7',
  mint: '#33AD7C',
  burgundy: '#970012',
  orange: '#F8772C',
  lightblue: '#428FE0',
}

@Injectable({ providedIn: 'root' })
export class ThemesManager extends ViewModel {
  protected readonly whiteLabelSettings = inject(WhiteLabelSettingsProvider)
  protected readonly localStorage = inject(BrowserLocalStorage)
  protected readonly themesPaletteCache = new Map<ThemeName | string, Promise<Palette>>()
  protected readonly PALETTE_CACHE_KEY = 'app.theme-palette'
  protected applyRequest = 0

  protected readonly store$ = {
    current: new BehaviorSubject<ThemeName>('purple'),
  }

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  @Memoize()
  public get state$() {
    return {
      ...exposeReadonlyState(this.store$, ['current']),
    }
  }

  /** Applies synchronously when the palette is stored, so only a new color waits for the generator. */
  public async apply(theme: ThemeName) {
    const request = ++this.applyRequest
    const stored = this.readStoredPalette()
    // Purple is the stylesheet default
    const palette =
      theme === 'purple'
        ? undefined
        : stored?.theme === theme
          ? stored.palette
          : await this.getThemePalette(theme)
    if (request !== this.applyRequest) return

    this.applyPalette(theme, palette)
    this.storePalette(theme, palette)
  }

  public getThemePalette(theme: ThemeName | string): Promise<Palette> {
    if (!this.themesPaletteCache.has(theme)) {
      const color = theme.includes('#') ? theme : PRE_DEFIED_COLOR_SCHEMES[theme as ThemeName]
      this.themesPaletteCache.set(
        theme,
        import('./theme-generator').then(({ simpleGeneratePalette }) =>
          simpleGeneratePalette(color),
        ),
      )
    }
    return this.themesPaletteCache.get(theme)!
  }

  protected applyPalette(theme: ThemeName, palette: Palette | undefined) {
    const root = document.documentElement
    const shades: Shade[] = [700, 600, 500, 400, 300, 200, 100, 50, 25]
    for (const shade of shades) {
      const value = palette?.[shade]
      if (value) {
        root.style.setProperty(`--color-primary-${shade}`, value)
      } else {
        root.style.removeProperty(`--color-primary-${shade}`)
      }
    }

    this.store$.current.next(theme)
  }

  protected override init() {
    // The white-label settings arrive from the API; the last palette keeps the brand colors meanwhile
    const stored = this.readStoredPalette()
    if (stored) {
      this.applyPalette(stored.theme as ThemeName, stored.palette)
    }

    this.whiteLabelSettings.data$.pipe(takeUntil(this.destroy$)).subscribe((settings) => {
      if (settings) {
        void this.apply((settings.colorScheme as ThemeName | undefined) || 'purple')
      }
    })
  }

  protected readStoredPalette(): StoredPalette | undefined {
    try {
      const stored = JSON.parse(this.localStorage.getItem(this.PALETTE_CACHE_KEY) ?? 'null')
      return stored?.theme && stored.palette ? stored : undefined
    } catch {
      return undefined
    }
  }

  protected storePalette(theme: ThemeName, palette: Palette | undefined) {
    if (palette) {
      this.localStorage.setItem(this.PALETTE_CACHE_KEY, JSON.stringify({ theme, palette }))
    } else {
      this.localStorage.removeItem(this.PALETTE_CACHE_KEY)
    }
  }
}
